import React, { useMemo, useState } from 'react';
import { Alert } from 'reactstrap';

import { getErrorMessage } from '~libs/axiosWrapper';
import { useToast } from '~libs/toast';

import { Button, ConfirmDialog, ErrorResponse, Spinner, Toolbar } from '~components';
import Switch from '~components/form-fields/inputs/Switch';
import Icon, { faPlus } from '~components/icon';

import { CarePlan, CarePlanLookup, CarePlanNeed, CarePlanServiceLine } from '~clients/models';

import {
  useDeleteCarePlanServiceLineMutation,
  useGetAppointmentServiceTypesQuery,
  useGetCalendarFrequenciesQuery,
  useGetCarePlanGoalsQuery,
  useGetCarePlanNeedsQuery,
  useGetCarePlanServiceLinesQuery,
  useGetGoalApproachTypesQuery,
  useGetGoalStatusesQuery,
} from '~clients/actions/carePlans';

import { bySequence } from '../reorder';

import ByGoalView from './ByGoalView';
import CoverageBar from './CoverageBar';
import ListView from './ListView';
import ServiceFormModal from './ServiceFormModal';

interface ComponentProps {
  plan: CarePlan;
}

type ServiceView = 'by-goal' | 'list';

const mapById = <Key, Value>(rows: Value[], keyOf: (row: Value) => Key): Map<Key, Value> =>
  new Map(rows.map((row) => [keyOf(row), row]));

/**
 * Services & Delivery tab: a coverage bar plus a switch between the "By goal" (default)
 * and "List" views of the plan's services. CRUD goes through the shared
 * carePlanServiceLine cache, so the Overview activation checklist recomputes on change.
 */
const ServicesDeliveryTab: React.FC<ComponentProps> = ({ plan }) => {
  const { data: serviceLines, isLoading: isLoadingServiceLines, error } = useGetCarePlanServiceLinesQuery({
    planId: plan.id,
  });
  const { data: goals, isLoading: isLoadingGoals, error: goalsError } = useGetCarePlanGoalsQuery({ planId: plan.id });
  const { data: needs } = useGetCarePlanNeedsQuery({ planId: plan.id });
  const { data: statuses } = useGetGoalStatusesQuery();
  const { data: approachTypes } = useGetGoalApproachTypesQuery();
  const { data: serviceTypes } = useGetAppointmentServiceTypesQuery();
  const { data: frequencies } = useGetCalendarFrequenciesQuery();

  const [deleteServiceLine, { isLoading: isDeleting, error: deleteError }] = useDeleteCarePlanServiceLineMutation();
  const { showToast } = useToast();

  const [view, setView] = useState<ServiceView>('by-goal');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editing, setEditing] = useState<CarePlanServiceLine | undefined>(undefined);
  const [addGoalId, setAddGoalId] = useState<string | null | undefined>(undefined);
  const [pendingDelete, setPendingDelete] = useState<CarePlanServiceLine | null>(null);

  // Wait on the goals query too — otherwise a fast service-lines response renders the
  // "add a goal first" empty state before goals arrive.
  const isLoading = isLoadingServiceLines || isLoadingGoals;

  // Either request failing must block the "add a goal first" empty state and the main
  // content — otherwise a failed goals fetch (goals -> []) masquerades as "no goals yet".
  const hasLoadError = error !== undefined || goalsError !== undefined;

  const lines = serviceLines ?? [];
  const goalList = goals ?? [];
  // Services link to goals, so at least one goal must exist before any can be added.
  const hasGoals = goalList.length > 0;

  // Lookup maps built once per data change; shared by both views + the list columns.
  const serviceTypeNameById = useMemo(
    () => new Map<number, string>((serviceTypes ?? []).map((type) => [type.id, type.name])),
    [serviceTypes],
  );
  const frequencyNameById = useMemo(
    () => new Map<string, string>((frequencies ?? []).map((frequency) => [frequency.id, frequency.name])),
    [frequencies],
  );
  const needsById = useMemo(() => mapById<string, CarePlanNeed>(needs ?? [], (need) => need.id), [needs]);
  const statusesById = useMemo(() => mapById<string, CarePlanLookup>(statuses ?? [], (row) => row.id), [statuses]);
  const approachTypesById = useMemo(
    () => mapById<string, CarePlanLookup>(approachTypes ?? [], (row) => row.id),
    [approachTypes],
  );
  // Numbered goal labels ("1. …") in display order — matches the Goals tab + form.
  const goalLabelById = useMemo(
    () => new Map<string, string>(bySequence(goalList).map((goal, index) => [goal.id, `${index + 1}. ${goal.description}`])),
    [goalList],
  );

  const isSaving = isDeleting;

  const openAdd = (goalId: string | null = null) => {
    setEditing(undefined);
    setAddGoalId(goalId);
    setIsModalOpen(true);
  };
  const openEdit = (line: CarePlanServiceLine) => {
    setEditing(line);
    setAddGoalId(undefined);
    setIsModalOpen(true);
  };

  const performDelete = async (line: CarePlanServiceLine) => {
    try {
      await deleteServiceLine(line.id).unwrap();
      showToast('success', 'Service deleted');
    } catch {
      // Surfaced through deleteError below.
    }
  };

  const onConfirmDelete = async (confirm: boolean) => {
    const target = pendingDelete;
    setPendingDelete(null);
    if (confirm && target) await performDelete(target);
  };

  return (
    <div className="section-card-container">
      {!isLoading && !hasLoadError && !hasGoals && (
        <Alert color="warning" className="care-plan-alert" data-cy="care-plan-coverage-empty">
          A goal must be added before services can be added and linked.
        </Alert>
      )}

      <Toolbar
        id="care-plan-service-lines"
        leftCluster={
          <div className="care-plan-service-view-toggle">
            <Switch
              type="switch"
              id="care-plan-service-view"
              name="care-plan-service-view"
              value={view === 'list'}
              labels={['Goal View', 'List View']}
              onChange={() => setView((prev) => (prev === 'by-goal' ? 'list' : 'by-goal'))}
            />
          </div>
        }
        rightCluster={
          <Button
            size="sm"
            color="success"
            dataCy="care-plan-service-add"
            disabled={!hasGoals}
            onClick={() => openAdd(null)}
          >
            <Icon className="me-2" icon={faPlus} />
            Add New Service
          </Button>
        }
      />

      {isLoading && (
        <div className="care-plan-section-loading">
          <Spinner loading />
        </div>
      )}
      {error !== undefined && <ErrorResponse dataCy="care-plan-service-lines-error" message={getErrorMessage(error)} />}
      {goalsError !== undefined && (
        <ErrorResponse dataCy="care-plan-service-lines-goals-error" message={getErrorMessage(goalsError)} />
      )}
      {deleteError !== undefined && (
        <ErrorResponse dataCy="care-plan-service-lines-save-error" message={getErrorMessage(deleteError)} />
      )}

      {!isLoading && !hasLoadError && (
        <>
          <CoverageBar goals={goalList} serviceLines={lines} needsById={needsById} />

          {view === 'by-goal' ? (
            <ByGoalView
              goals={goalList}
              serviceLines={lines}
              serviceTypesById={serviceTypeNameById}
              frequenciesById={frequencyNameById}
              needsById={needsById}
              statusesById={statusesById}
              approachTypesById={approachTypesById}
              onAddForGoal={openAdd}
              onEdit={openEdit}
              onDelete={setPendingDelete}
              isSaving={isSaving}
            />
          ) : (
            <ListView
              serviceLines={bySequence(lines)}
              serviceTypesById={serviceTypeNameById}
              frequenciesById={frequencyNameById}
              goalLabelById={goalLabelById}
              onEdit={openEdit}
              onDelete={setPendingDelete}
              isSaving={isSaving}
              isLoading={isLoading}
            />
          )}
        </>
      )}

      <ServiceFormModal
        key={editing?.id ?? `new-${addGoalId ?? 'none'}`}
        planId={plan.id}
        serviceLine={editing}
        defaultGoalId={addGoalId}
        isOpen={isModalOpen}
        setIsOpen={setIsModalOpen}
      />
      <ConfirmDialog
        isOpen={pendingDelete !== null}
        useList={false}
        messages={
          <p>
            <strong>Warning:</strong> This service will be deleted.
          </p>
        }
        onClose={onConfirmDelete}
      />
    </div>
  );
};

export default ServicesDeliveryTab;
